import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import Select, { SingleValue } from 'react-select';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';

import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';

import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import Button from 'common/button/button';
import CheckBox from 'common/checkbox/checkbox';
import MonthInput from 'common/month-input/month-input';
import ToggleButton from 'components/toggleButton/ToggleButton';
import './transfer-of-mgt.scss';
import {
  IEmpInPayTransfer,
  IFormDataInPaystatement,
  IPayItemInPaystatement,
  ISendHistoryItem,
  IStatistics,
} from 'types/salary';
import styleGuide from 'constants/styleGuide';
import Statistics from 'components/salary/Statistics';
import CommonModal from 'common/CommonModal';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import SendHistoryTable from 'components/salary/SendHistoryTable';
import { addComma } from 'constants/utils';
import PayTable from 'components/salary/PayTable';
import SectionPopup from 'components/salary/SectionPopup';
import { IOption } from 'types/common';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedDate } from '../../../state/salarySlice';
import { IRootState } from '~/state/store';

const EmptyEmpList = styled.div`
  display: flex;
  position: absolute;
  margin-top: 10%;
  width: 100%;
  flex-direction: column;
  justify-content: center;
`;
const EmptyEmpListText = styled.span`
  color: ${styleGuide.palette.greyColor};
`;
const TableWrap = styled.div`
  border-radius: 6px;
  box-shadow: 0 0 0 1px #e9e9e9;
`;

const payTypeOptions = [
  { value: '0', label: '시급' },
  { value: '1', label: '일급' },
  { value: '2', label: '월급' },
  { value: '3', label: '직접입력' },
];

const TransferOfMgt: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedDate } = useSelector((state: IRootState) => state.salary);
  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiNode, appApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [date, setDate] = React.useState<Date>(new Date(selectedDate));
  // 전월 대비 급여 통계
  const [payStatistics, setPayStatistics] = React.useState<IStatistics>({
    show: false,
    prevEmps: 0,
    prevPayed: 0,
    prevDeductions: 0,
    prevRealPayed: 0,
    monthEmps: 0,
    monthPayed: 0,
    monthDeductions: 0,
    monthRealPayed: 0,
  });
  const [empList, setEmpList] = React.useState<Array<IEmpInPayTransfer>>([]); // 직원 목록
  const [allEmployeeSelectCheckbox, setAllEmployeeSelectCheckbox] = React.useState<boolean>(false); // 전직원 체크 여부
  const [sendHistoryData, setSendHistoryData] = React.useState<{
    modalShow: boolean; // 급여명세서 전송 내역 모달 표시 여부
    memberIndex: number; // 선택한 직원명
    empName: string; // 선택한 직원명
    data: ISendHistoryItem[]; // 전송내역 데이터
  }>({
    modalShow: false,
    memberIndex: 0,
    empName: '',
    data: [],
  });

  const [selectedEmpInfo, setSelectedEmpInfo] = React.useState<IEmpInPayTransfer | null>(null); // 직원목록에서 클릭한 직원 정보(급여표시)
  const [payType, setPayType] = React.useState<{ value: string; label: string }>({ value: '0', label: '시급' }); // 급여유형
  const [payData, setPayData] = React.useState<IPayItemInPaystatement[]>([]); // 지급급여
  const [paySecondData, setPaySecondData] = React.useState<IPayItemInPaystatement[]>([]); // 기본급2급여
  const [deducData, setDeducData] = React.useState<IPayItemInPaystatement[]>([]); // 공제급여
  const [payFormData, setPayFormData] = React.useState<IFormDataInPaystatement>({}); // 급여 양식 데이터
  const [payFormSecondData, setPayFormSecondData] = React.useState<IFormDataInPaystatement>({}); // 기본급2 데이터
  const [useSecondDefaultPay, setUseSecondDefaultPay] = React.useState<boolean>(false); // 기본급2사용 여부
  const [sectionType, setSectionType] = React.useState<'지급' | '공제'>('지급');
  const [sectionPopupShow, setSectionPopupShow] = React.useState<boolean>(false);
  const [popupLog, setPopupLog] = React.useState<boolean>(true); // 요율 변경 알림 팝업

  const navigate = useNavigate();

  const enterPayEmpListScreen = () => {
    navigate('/salary-specification-emp-list');
  };
  const enterDetailPayScreen = (pkIndex: number = 0) => {
    navigate('/salary-detail', {
      state: {
        memberIndex: sendHistoryData.memberIndex > 0 ? sendHistoryData.memberIndex : selectedEmpInfo?.MEMBER_INDEX,
        date,
        pkIndex,
        fromType: '1', // 직원목록에서 이동
      },
    });
  };

  // 급여명세서 직원 목록 패칭
  const fetchEmpList = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      setAllEmployeeSelectCheckbox(false);
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getPaystubMemberMonthV2`, {
        SELECT_DATE: moment(date).format('YYYYMM'),
      });

      if (data.resultCode === 0) {
        setEmpList(
          data.resultData.DATA.filter((i: IEmpInPayTransfer) => {
            if (i.RETIRE_DATE) {
              const limitDate = moment(date).date(1).subtract(1, 'day').format('YYYYMMDD');
              return moment(i.RETIRE_DATE).isAfter(moment(limitDate));
            } else {
              return i;
            }
          }).map((i: IEmpInPayTransfer) => ({ ...i, isChecked: false })),
        );
        const selectedEmpIdx = data.resultData.DATA.find(
          (i: IEmpInPayTransfer) => i.MEMBER_INDEX === selectedEmpInfo?.MEMBER_INDEX,
        );
        if (selectedEmpIdx) {
          setSelectedEmpInfo(selectedEmpIdx);
        } else {
          setSelectedEmpInfo(data.resultData.DATA[0]);
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'BusinessEstablishmentChoice /auth/getPaystubMemberMonthV2');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 통계 데이터 패칭
  const fetchStatisticData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getStatisticsV2`, {
        SELECT_DATE: moment(date).format('YYYYMM'),
      });

      if (data.resultCode === 0) {
        const { LAST_WEEK_DEDUC, LAST_WEEK_PAY, LAST_WEEK_WORKER, THIS_WEEK_DEDUC, THIS_WEEK_PAY, THIS_WEEK_WORKER } =
          data.resultData;

        setPayStatistics({
          show: true,
          prevEmps: LAST_WEEK_WORKER,
          prevPayed: LAST_WEEK_PAY,
          prevDeductions: LAST_WEEK_DEDUC,
          prevRealPayed: LAST_WEEK_PAY - LAST_WEEK_DEDUC,
          monthEmps: THIS_WEEK_WORKER,
          monthPayed: THIS_WEEK_PAY,
          monthDeductions: THIS_WEEK_DEDUC,
          monthRealPayed: THIS_WEEK_PAY - THIS_WEEK_DEDUC,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'BusinessEstablishmentChoice /auth/getStatisticsV2');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 통계 모달 토글
  const toggleStatisticModal = () => {
    setPayStatistics((prev) => ({ ...prev, show: !prev.show }));
  };

  // 직원 카카오톡 전송 선택 체크
  const checkboxInviteToggle = (memberIdx: number) => {
    const empData = empList.map((i: IEmpInPayTransfer) => {
      if (i.MEMBER_INDEX === memberIdx) {
        return { ...i, isChecked: !i.isChecked };
      }
      return i;
    });
    setEmpList(empData);
  };

  // 전체 직원 카카오톡 전송 선택 체크
  const checkboxEmployeeAllToggle = () => {
    setAllEmployeeSelectCheckbox(!allEmployeeSelectCheckbox);
    const empData = empList.map((i) => {
      return { ...i, isChecked: allEmployeeSelectCheckbox ? false : true };
    });
    setEmpList(empData);
  };

  // 카카오톡 전송 버튼 클릭 시 데이터 검증
  const clickSendKakao = () => {
    const checkedEmps = empList.filter((i) => i.isChecked).map((i) => ({ seq: i.MEMBER_INDEX, name: i.NAME }));
    if (checkedEmps.length === 0) {
      return setModalState({
        isModalShow: true,
        title: '급여명세서 전송',
        desc: `등록된 직원 목록에서 전송할 직원의 체크박스에 체크해 주세요.`,
      });
    } else {
      return setModalState({
        isModalShow: true,
        title: '급여명세서 전송',
        desc: `직원 목록에서 체크표시 된 ${checkedEmps.length}명의 직원들에게 급여명세서를 전송합니다.\n\n${checkedEmps
          .map((i) => i.name)
          .join(' / ')}`,
        okBtnText: '확인',
        okCallback: () => sendPaybyKakao(checkedEmps.map((i) => i.seq)),
      });
    }
  };

  // 급여명세서 카카오톡으로 전송 요청
  const sendPaybyKakao = async (list: number[]) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/pushPayKakao`, {
        SELECT_DATE: moment(date).format('YYYYMM'),
        MEMBER_ARRAY: list,
      });

      if (data.resultCode === 0) {
        if (data.resultData.MESSAGE === 'SUCCESS') {
          fetchEmpList();
          setModalState({
            isModalShow: true,
            title: '급여명세서 전송 완료',
            desc: `해당 직원에게 카카오톡으로 급여명세서를 발송하였습니다`,
          });
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'transfer-of-mgt /auth/pushPayKakao');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 전송 내역 확인 버튼 클릭
  const clickSendHistory = (empData: IEmpInPayTransfer) => {
    if (empData.LAST_PUSH_DATE !== '-') {
      getSendHistory(empData.MEMBER_INDEX, empData.NAME);
    } else {
      return setModalState({
        isModalShow: true,
        title: '급여명세서 전송내역 보기',
        desc: `${moment(date).format('YYYY년 M월')} ${empData.NAME} 직원의 전송이력이 없습니다.`,
      });
    }
  };

  // 급여명세서 전송 내역 데이터 패칭
  const getSendHistory = async (memberIndex: number, empName: string) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getPaystubMemberPushList`, {
        MEMBER_INDEX: memberIndex.toString(),
        SELECT_DATE: moment(date).format('YYYY-MM'),
      });

      if (data.resultCode === 0) {
        if (data.resultData.MESSAGE === 'SUCCESS') {
          setSendHistoryData({ modalShow: true, memberIndex, empName, data: data.resultData.DATA });
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'transfer-of-mgt /auth/getPaystubMemberPushList');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 전송 내역 모달 표시 토글
  const toggleSendHistoryModalVisible = () => {
    setSendHistoryData((prevData) => {
      return {
        ...prevData,
        modalShow: !prevData.modalShow,
      };
    });
  };

  // 직원목록에서 직원 선택
  const selectEmp = (empData: IEmpInPayTransfer) => {
    setSelectedEmpInfo(empData);
  };

  // 선택 직원 급여 데이터 패칭
  const fetchEmpPay = async (isNotFirstLoad = false, callPrevMonth = false) => {
    try {
      if (!selectedEmpInfo) return false;
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getPaystubMemberDetailV2`, {
        SELECT_DATE: callPrevMonth ? moment(date).subtract(1, 'M').format('YYYYMM') : moment(date).format('YYYYMM'),
        MEMBER_INDEX: selectedEmpInfo.MEMBER_INDEX.toString(),
      });

      if (data.resultCode === 0) {
        if (data.resultData.MESSAGE === 'SUCCESS') {
          const { PAY_TYPE, PAY_ARRAY, PAY_ARRAY2, DEDUC_ARRAY } = data.resultData.DATA;
          setPayType(payTypeOptions.find((i) => i.value === PAY_TYPE) ?? payTypeOptions[0]);
          setPayData(
            PAY_ARRAY.map((product: IPayItemInPaystatement, index: number) => ({
              ...product,
              type: index < 8 ? '0' : '1',
            })),
          );
          if (PAY_ARRAY2 !== null) setUseSecondDefaultPay(true);
          setPaySecondData([
            {
              ITEM_INDEX: '0',
              ITEM: '기본급2',
              VALUE: PAY_ARRAY2 === null ? '0' : PAY_ARRAY2,
              USE_YN: 'Y',
              type: '0',
            },
          ]);
          setDeducData(
            DEDUC_ARRAY.map((deduc: IPayItemInPaystatement, index: number) => {
              let isDuplicated = false;
              PAY_ARRAY.map((product: IPayItemInPaystatement) => {
                if (deduc.ITEM === product.ITEM) {
                  isDuplicated = true;
                }
              });
              return {
                ITEM_INDEX: deduc.ITEM_INDEX,
                ITEM: `${deduc.ITEM}${isDuplicated ? '_D' : ''}`,
                VALUE: Math.abs(Number(deduc.VALUE)).toString(),
                USE_YN: deduc.USE_YN,
                type: index < 11 ? '0' : '1',
                isMinus: Number(deduc.VALUE) < 0 ? true : false,
              };
            }),
          );
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'transfer-of-mgt /auth/getPaystubMemberDetailV2');
    } finally {
      setLoadingState({ isFetchLoading: false });
      if (popupLog && moment(date).year() === new Date().getFullYear() && moment().format('MM') == '01') {
        setPopupLog(false);
        setTimeout(() => {
          setModalState({
            isModalShow: true,
            title: `${moment().format('YYYY')}년 급여명세서`,
            desc: `${moment().format('YYYY')}년 변경된 요율이 적용됩니다.`,
          });
        }, 300);
      }
      if (isNotFirstLoad) {
        setTimeout(() => {
          window?.document?.getElementById('screen')?.scrollTo({
            top: window?.document.getElementById('SendStatementWrap')?.offsetHeight ?? 0,
            behavior: 'smooth',
          });
        }, 300);
      }
    }
  };

  // 출퇴근 기반 급여정보 불러오기
  const confirmGetRealPayByCommute = () => {
    if (!selectedEmpInfo) {
      setModalState({
        isModalShow: true,
        title: `출퇴근 기반 급여 불러오기`,
        desc: `왼쪽 직원목록에서 직원을 선택해 주세요`,
      });
    } else {
      setModalState({
        isModalShow: true,
        title: `출퇴근 기반 급여 불러오기`,
        desc: `- 직원의 출퇴근 기반 데이터(근무시간,근무일수,시급,일급,월급 등)를 불러옵니다.\n\n -선택한 직원이 사업주로부터 초대를 받고 사업장 출퇴근을 진행한 경우에만 불러올 수 있습니다.\n\n -정산 탭의 [급여대장] 데이터와 동일하며, [급여대장] 시급직의 경우 일별 급여액(출퇴근 시간 x 시급)을 합산하여 계산합니다. 따라서 원단위가 절삭되어 현재 선택된 월의 [급여명세서] 기본급과 [급여대장] 기본급 금액의 차이가 발생할 수 있습니다.\n\n -데이터를 불러온 후 [공제 계산하기] 버튼을 눌러 급여를 계산하고 저장해 주세요.`,
        okBtnText: '확인',
        okCallback: getRealPayByCommute,
      });
    }
    // setModalState({
    //   isModalShow: true,
    //   title: `출퇴근 기반 급여 불러오기`,
    //   desc: `해당 기능은 점검중입니다.\n\n*12월1일 17시 오픈예정.`,
    //   okBtnText: '확인',
    //   okCallback: getRealPayByCommute,
    // });
  };

  // 출퇴근 기반 급여정보 불러오기
  const getRealPayByCommute = async () => {
    const { data }: AxiosResponse['data'] = await appApiNode('post', `/auth/getPaystubMemberPayInfo`, {
      MEMBER_INDEX: selectedEmpInfo?.MEMBER_INDEX,
      SELECT_DATE_YEAR: moment(date).format('YYYY'),
      SELECT_DATE_MONTH: moment(date).format('MM'),
    });
    //{"result":"SUCCESS","PAY":"10000","HOURS":99,"MINUTES":40,"COUNT":22,"TOTAL_PAY":996670,"WEEK_HOURS":69,"WEEK_MINUTES":"0"} {
    if (data.result === 'SUCCESS') {
      if (payType.label === '시급') {
        setPayFormData((prev) => ({
          ...prev,
          근무시간: data.HOURS,
          근무분: data.MINUTES,
          시급: data.PAY,
          주휴수당시간: data.WEEK_HOURS,
          주휴수당분: data.WEEK_MINUTES,
          기본급: addComma((Number(data.TOTAL_PAY) + Number(data.WEEK_PAY)).toString()),
        }));
      } else if (payType.label === '일급') {
        setPayFormData((prev) => ({
          ...prev,
          근무일수: data.COUNT,
          일급: data.PAY,
          기본급: addComma((Number(data.PAY) * Number(data.COUNT)).toString()),
        }));
      } else {
        setPayFormData((prev) => ({
          ...prev,
          근무시간: data.HOURS,
          근무분: data.MINUTES,
          기본급: addComma(data.PAY),
        }));
      }
    } else {
      setModalState({
        isModalShow: true,
        title: `출퇴근 기반 급여 불러오기`,
        desc: `해당 직원의 출퇴근 기록이 없습니다`,
      });
    }
  };

  // 월 변경
  const handleCalendarInput = (date: Date) => {
    setDate(date);
    dispatch(setSelectedDate(date.toISOString()));
  };

  // 급여유형 변경
  const handlePayType = (
    newValue: SingleValue<{
      value: string;
      label: string;
    }>,
  ) => {
    if (newValue) setPayType(newValue);
  };
  // 정산 항목인 경우 +/- 형식 토글
  const plusMinusToggle = (name: string) => {
    setDeducData((prevState) => {
      let UPDATE_DEDUC = prevState.map((deduc) => {
        if (deduc.ITEM == name) {
          return { ...deduc, isMinus: !deduc.isMinus };
        } else {
          return deduc;
        }
      });
      return UPDATE_DEDUC;
    });
  };

  // 시간,분,시급,일급 등 입력 인풋 blur 시 금액 자동합계
  const payAutoSum = (isSecondDefaultPay = false) => {
    let SUM = 0;
    let SUM2 = 0;

    if (payType.label == '시급') {
      const pay = payFormData['시급'] ? payFormData['시급'].split(',').join('') : '0';
      const hour = payFormData['근무시간'];
      const min = payFormData['근무분'];
      const paySum = Math.ceil((Number(pay) * Number(hour) + (Number(pay) * Number(min)) / 60) / 10) * 10;
      const weekHourPay = (Number(pay) * 8 * Number(payFormData['주휴수당시간'] ?? 0)) / 40;
      const weekMinPay = (Number(pay) * 8 * (Number(payFormData['주휴수당분'] ?? 0) / 60)) / 40;
      const weekPaySum = Math.ceil((weekHourPay + weekMinPay) / 10) * 10;

      if (pay == '0') {
        setPayFormData((prevData) => ({ ...prevData, 기본급: '0' }));
      } else {
        SUM = paySum + weekPaySum;
        setPayFormData((prevData) => ({ ...prevData, 기본급: addComma(SUM.toString()) }));
      }

      if (isSecondDefaultPay) {
        const pay2 = payFormSecondData['시급'] ? payFormSecondData['시급'].split(',').join('') : '0';
        const hour2 = payFormSecondData['근무시간'];
        const min2 = payFormSecondData['근무분'];
        const paySum2 = Math.ceil((Number(pay2) * Number(hour2) + (Number(pay2) * Number(min2)) / 60) / 10) * 10;
        const weekHourPay2 = (Number(pay2) * 8 * Number(payFormSecondData['주휴수당시간'])) / 40;
        const weekMinPay2 = (Number(pay2) * 8 * (Number(payFormSecondData['주휴수당분']) / 60)) / 40;
        const weekPaySum2 = Math.ceil((weekHourPay2 + weekMinPay2) / 10) * 10;
        if (pay2 == '0') {
          setPayFormSecondData((prevData) => ({ ...prevData, 기본급: '0' }));
        } else {
          SUM2 = paySum2 + weekPaySum2;
          setPayFormSecondData((prevData) => ({ ...prevData, 기본급: addComma(SUM2.toString()) }));
        }
      }
    }
    if (payType.label == '일급') {
      if (payFormData['일급'] !== '0' && payFormData['근무일수'] !== '0') {
        SUM = Number(payFormData['일급'].split(',').join('')) * Number(payFormData['근무일수']);
      } else {
        SUM = 0;
      }

      setPayFormData((prevData) => ({ ...prevData, 기본급: addComma(SUM.toString()) }));
    }
  };

  const AddZeroValue = (NAME: string) => {
    if (payFormData[NAME] == '') {
      setPayFormData((prevData) => {
        return { ...prevData, [NAME]: '0' };
      });
    }
    if (payFormSecondData[NAME] && payFormSecondData[NAME] === '') {
      setPayFormSecondData((prevData) => {
        return { ...prevData, [NAME]: '0' };
      });
    }
  };

  const colourStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '40px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  // 시급-기본급2 사용여부 토글
  const toggleUseSecondDefaultPay = React.useCallback(
    (checked: boolean) => {
      setUseSecondDefaultPay(checked);
    },
    [useSecondDefaultPay],
  );

  // 급여 내역 값 저장
  const paySaveAndDeducCalculate = async (type: '지급' | '공제' | '초기화') => {
    try {
      setLoadingState({ isFetchLoading: true });

      let PAY_ARRAY: IPayItemInPaystatement[] = [];
      let PAY_ARRAY2 = null;
      let DEDUC_ARRAY: IPayItemInPaystatement[] = [];

      if (selectedEmpInfo === null) {
        return setModalState({
          isModalShow: true,
          title: '급여 초기화',
          desc: `등록된 직원 목록에서 초기화할 직원을 선택해 주세요`,
        });
      } else {
        if (type === '초기화') {
          PAY_ARRAY = payData.map((item, index) => {
            if (item.ITEM == '기본급') {
              if (payType.label == '시급') {
                return {
                  ITEM: item.ITEM,
                  VALUE: `0,0,0,0,0,0`,
                  USE_YN: item.USE_YN,
                };
              } else {
                return { ITEM: item.ITEM, VALUE: `0,0,0`, USE_YN: item.USE_YN };
              }
            } else if (item.ITEM == '연장근로수당') {
              return { ITEM: item.ITEM, VALUE: `0,0,0`, USE_YN: item.USE_YN };
            } else if (item.ITEM == '야간근로수당') {
              return { ITEM: item.ITEM, VALUE: `0,0,0`, USE_YN: item.USE_YN };
            } else if (item.ITEM == '휴일근로수당') {
              return { ITEM: item.ITEM, VALUE: `0,0,0`, USE_YN: item.USE_YN };
            } else {
              return { ITEM: item.ITEM, VALUE: `0`, USE_YN: item.USE_YN };
            }
          });

          DEDUC_ARRAY = deducData.map((item, index) => {
            return {
              ITEM: item.ITEM.replace('_D', ''),
              VALUE: '0',
              USE_YN: item.USE_YN,
            };
          });
        } else {
          // 급여명세서 수정
          // 유효성 검사
          let regex = {
            check: true,
            title: '',
            text: '',
          };

          if (payFormData['기본급'] == '0' || payFormData['기본급'] == '') {
            regex = {
              check: false,
              title: '급여명세서 작성',
              text: '기본급을 입력해 주세요.',
            };
          }

          if (payType.label == '시급') {
            if (payFormData['근무시간'] == '0' || payFormData['근무시간'] == '') {
              regex = {
                check: false,
                title: '급여명세서 작성 - 근무시간',
                text: '근무시간을 입력해 주세요.',
              };
            }
            if (payFormData['시급'] == '0' || payFormData['시급'] == '') {
              regex = {
                check: false,
                title: '급여명세서 작성 - 시급',
                text: '시급을 입력해 주세요.',
              };
            }
            if (useSecondDefaultPay) {
              if (payFormSecondData['근무시간'] == '0' || payFormSecondData['근무시간'] == '') {
                regex = {
                  check: false,
                  title: '급여명세서 작성 - 기본급2 근무시간',
                  text: '기본급2 근무시간을 입력해 주세요. 기본급2를 사용하지 않으시려면 체크해제해 주세요.',
                };
              }
              if (payFormSecondData['시급'] == '0' || payFormSecondData['시급'] == '') {
                regex = {
                  check: false,
                  title: '급여명세서 작성 - 기본급2 시급',
                  text: '기본급2 시급을 입력해 주세요. 기본급2를 사용하지 않으시려면 체크해제해 주세요.',
                };
              }
            }
          } else if (payType.label == '일급') {
            if (payFormData['근무일수'] == '0' || payFormData['근무일수'] == '') {
              regex = {
                check: false,
                title: '급여명세서 작성 - 일급',
                text: '근무일수를 입력해 주세요.',
              };
            }
            if (payFormData['일급'] == '0' || payFormData['일급'] == '') {
              regex = {
                check: false,
                title: '급여명세서 작성 - 일급',
                text: '일급을 입력해 주세요.',
              };
            }
          } else {
            if (payFormData['근무시간'] == '') {
              regex = {
                check: false,
                title: '급여명세서 작성 - 직접입력',
                text: '근무시간을 입력해 주세요.',
              };
            }
          }

          if (!regex.check) {
            return setModalState({
              isModalShow: true,
              title: `${regex.title}`,
              desc: `${regex.text}`,
            });
          }

          PAY_ARRAY = payData.map((item, index) => {
            let SUM = payFormData[item.ITEM] == '' ? '0' : payFormData[item.ITEM];
            if (!SUM) {
              SUM = '0';
            } else if (isNaN(Number(SUM.toString().split(',').join('')))) {
              SUM = '0';
            } else {
              SUM = SUM.toString().split(',').join('');
            }

            let VALUE1 = '';
            let VALUE2 = '';
            let VALUE3 = '';
            let VALUE4 = '';
            let VALUE5 = '';

            if (item.ITEM == '기본급') {
              if (payType.label == '시급') {
                VALUE1 = payFormData['근무시간'] == '' ? '0' : payFormData['근무시간'];
                VALUE2 = payFormData['근무분'] == '' ? '0' : payFormData['근무분'];
                VALUE3 = payFormData['시급'] == '' ? '0' : payFormData['시급'].split(',').join('');
                VALUE4 = payFormData['주휴수당시간'] == '' ? '0' : payFormData['주휴수당시간'];
                VALUE5 = payFormData['주휴수당분'] == '' ? '0' : payFormData['주휴수당분'];
                return {
                  ITEM: item.ITEM,
                  VALUE: `${VALUE1},${VALUE2},${VALUE3},${VALUE4},${VALUE5},${SUM}`,
                  USE_YN: item.USE_YN,
                };
              } else if (payType.label == '일급') {
                VALUE1 = payFormData['근무일수'] == '' ? '0' : payFormData['근무일수'];
                VALUE2 = payFormData['일급'] == '' ? '0' : payFormData['일급'].split(',').join('');
                return {
                  ITEM: item.ITEM,
                  VALUE: `${VALUE1},${VALUE2},${SUM}`,
                  USE_YN: item.USE_YN,
                };
              } else {
                VALUE1 = payFormData['근무시간'] == '' ? '0' : payFormData['근무시간'];
                VALUE2 = payFormData['근무분'] == '' ? '0' : payFormData['근무분'];
                return {
                  ITEM: item.ITEM,
                  VALUE: `${VALUE1},${VALUE2},${SUM}`,
                  USE_YN: item.USE_YN,
                };
              }
            } else if (item.ITEM == '연장근로수당') {
              VALUE1 = payFormData['연장근로시간'] == '' ? '0' : payFormData['연장근로시간'];
              VALUE2 = payFormData['연장근로분'] == '' ? '0' : payFormData['연장근로분'];
              return {
                ITEM: item.ITEM,
                VALUE: `${VALUE1},${VALUE2},${SUM}`,
                USE_YN: item.USE_YN,
              };
            } else if (item.ITEM == '야간근로수당') {
              VALUE1 = payFormData['야간근로시간'] == '' ? '0' : payFormData['야간근로시간'];
              VALUE2 = payFormData['야간근로분'] == '' ? '0' : payFormData['야간근로분'];
              return {
                ITEM: item.ITEM,
                VALUE: `${VALUE1},${VALUE2},${SUM}`,
                USE_YN: item.USE_YN,
              };
            } else if (item.ITEM == '휴일근로수당') {
              VALUE1 = payFormData['휴일근로시간'] == '' ? '0' : payFormData['휴일근로시간'];
              VALUE2 = payFormData['휴일근로분'] == '' ? '0' : payFormData['휴일근로분'];
              return {
                ITEM: item.ITEM,
                VALUE: `${VALUE1},${VALUE2},${SUM}`,
                USE_YN: item.USE_YN,
              };
            } else {
              return {
                ITEM: item.ITEM,
                VALUE: `${SUM}`,
                USE_YN: item.USE_YN,
              };
            }
          });

          if (useSecondDefaultPay) {
            const hourPay2 = payFormSecondData['시급'].split(',').join('');
            const pay2 = payFormSecondData['기본급'].split(',').join('');
            PAY_ARRAY2 = [
              {
                VALUE: `${payFormSecondData['근무시간'] === '' ? '0' : payFormSecondData['근무시간']},${
                  payFormSecondData['근무분'] === '' ? '0' : payFormSecondData['근무분']
                },${hourPay2},${payFormSecondData['주휴수당시간'] === '' ? '0' : payFormSecondData['주휴수당시간']},${
                  payFormSecondData['주휴수당분'] === '' ? '0' : payFormSecondData['주휴수당분']
                },${pay2}`,
              },
            ];
          }

          DEDUC_ARRAY = deducData.map((item, index) => {
            let SUM = payFormData[item.ITEM].toString().split(',').join('');
            if (isNaN(Number(SUM))) SUM = '0';
            if (item.isMinus) {
              return {
                ITEM: item.ITEM.replace('_D', ''),
                VALUE: `-${SUM}`,
                USE_YN: item.USE_YN,
              };
            } else {
              return {
                ITEM: item.ITEM.replace('_D', ''),
                VALUE: SUM,
                USE_YN: item.USE_YN,
              };
            }
          });
        }
      }

      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updatePaystubMemberV2`, {
        SELECT_DATE: moment(date).format('YYYYMM'),
        DATA: [
          {
            MEMBER_INDEX: selectedEmpInfo.MEMBER_INDEX,
            SEPARATOR: type == '지급' ? 'Y' : 'N', // 지급수정 'Y', 공제수정 'N'
            PAY_ARRAY: PAY_ARRAY,
            PAY_ARRAY2: PAY_ARRAY2,
            DEDUC_ARRAY: DEDUC_ARRAY,
            PAY_TYPE: payType.value,
          },
        ],
      });

      if (data.resultCode === 0) {
        fetchEmpPay(true);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'transfer-of-mgt /auth/updatePaystubMemberV2');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 지난달 급여 불로오기 확인 모달
  const getPrevMonthPayData = () => {
    if (!selectedEmpInfo) {
      setModalState({
        isModalShow: true,
        title: `지난달 급여 불러오기`,
        desc: `왼쪽 직원목록에서 직원을 선택해 주세요`,
      });
    } else {
      setModalState({
        isModalShow: true,
        title: `지난달 급여 불러오기`,
        desc: `지난달 작성된 급여가 있는 경우에만 데이터를 불러오며 이후 저장하기 버튼을 눌러야 저장됩니다.`,
        okBtnText: '확인',
        okCallback: () => fetchEmpPay(false, true),
      });
    }
  };

  // 급여명세서 초기화 확인 모달
  const resetPay = () => {
    if (!selectedEmpInfo) {
      setModalState({
        isModalShow: true,
        title: `급여명세서 초기화`,
        desc: `왼쪽 직원목록에서 직원을 선택해 주세요`,
      });
    } else {
      setModalState({
        isModalShow: true,
        title: `급여명세서 초기화`,
        desc: `${moment(date).format('YYYY년 MM월')} ${selectedEmpInfo.NAME}직원의 급여명세서를 초기화 합니다`,
        okBtnText: '확인',
        okCallback: () => paySaveAndDeducCalculate('초기화'),
        isWarning: true,
      });
    }
  };

  // 지급,공제항목 팝업 토글
  const toggleSectionPopup = (type?: '지급' | '공제') => {
    if (!selectedEmpInfo) {
      setModalState({
        isModalShow: true,
        title: `${type}항목 관리`,
        desc: `등록된 직원이 없습니다. 직원등록 화면으로 이동하시겠습니까?`,
        okBtnText: '확인',
        okCallback: enterPayEmpListScreen,
        isWarning: true,
      });
    } else {
      setSectionType(type ? type : sectionType);
      setSectionPopupShow(!sectionPopupShow);
    }
  };

  const toggleSectionItem = (data: IPayItemInPaystatement[], item: IPayItemInPaystatement) => {
    let obj = { ITEM: '', ITEM_INDEX: '', USE_YN: '' };
    let list = data.map((_item, _i) => {
      if (item.ITEM == _item.ITEM) {
        obj = {
          ITEM: _item.ITEM,
          ITEM_INDEX: _item.ITEM_INDEX ?? '',
          USE_YN: _item.USE_YN == 'Y' ? 'N' : 'Y',
        };
        if (item.ITEM == '연장근로수당' || item.ITEM == '야간근로수당' || item.ITEM == '휴일근로수당') {
          return {
            ..._item,
            VALUE: '0,0,0',
            USE_YN: _item.USE_YN == 'Y' ? 'N' : 'Y',
          };
        } else {
          return {
            ..._item,
            VALUE: '0',
            USE_YN: _item.USE_YN == 'Y' ? 'N' : 'Y',
          };
        }
      } else {
        return _item;
      }
    });
    if (sectionType == '지급') {
      setPayData(list);
    } else {
      setDeducData(list);
    }
    UpdateSections(obj, list);
  };

  // 기타 항목 추가
  const addSection = (text: string) => {
    if (text === '') {
      return setModalState({
        isModalShow: true,
        title: `${sectionType == '지급' ? '지급' : '공제'}항목 추가`,
        desc: `항목을 입력해 주세요`,
      });
    }
    const trimedValue = text.trim();
    let data = sectionType == '지급' ? payData : deducData;
    let list = [];
    let ExsistFlag = { FORMULA: '', ITEM: '', ITEM_INDEX: '' };
    let DuplicateFlag = false;
    let obj = { ITEM: '', ITEM_INDEX: '', USE_YN: '' };
    data.map((item) => {
      // 삭제처리 전 아이템이 있는지 체크
      if (item.USE_YN == 'D' && item.ITEM.replace('_D', '') == trimedValue) {
        ExsistFlag = { FORMULA: item.FORMULA ?? '', ITEM: item.ITEM ?? '', ITEM_INDEX: item.ITEM_INDEX ?? '' };
      }
    });
    // 삭제되어있는 중복되는 값이 있는지 체크
    if (ExsistFlag.ITEM !== '') {
      list = data.map((item) => {
        if (item.USE_YN == 'D' && ExsistFlag.ITEM == item.ITEM) {
          obj = { ITEM_INDEX: ExsistFlag.ITEM_INDEX, ITEM: item.ITEM, USE_YN: 'N' };
          return { ...item, isActive: 'N' };
        } else {
          return item;
        }
      });
    } else {
      list = data.map((item) => {
        if (item.ITEM.replace('_D', '') == trimedValue) {
          DuplicateFlag = true;
        }
        return item;
      });
      if (DuplicateFlag) {
        return setModalState({
          isModalShow: true,
          title: `${sectionType == '지급' ? '지급' : '공제'}항목 추가`,
          desc: `이미 등록된 항목입니다`,
        });
      } else {
        list = data;
        obj = { ITEM_INDEX: '', ITEM: `${trimedValue}`, USE_YN: 'N' };
        list.push({
          ITEM_INDEX: '', // 수정필요?
          ITEM: `${trimedValue}`,
          VALUE: '0',
          USE_YN: 'N',
          type: '1',
        });
      }
    }

    if (sectionType == '지급') {
      setPayData(list);
    } else {
      setDeducData(list);
    }
    UpdateSections({ ...obj, ITEM: obj.ITEM.replace('_D', '') ?? '' }, list);
  };

  // 기타 항목 삭제
  const deleteSection = (name: string) => {
    let data = sectionType == '지급' ? payData : deducData;
    let obj = { ITEM: '', ITEM_INDEX: '', USE_YN: '' };
    let list = data.map((item) => {
      if (item.ITEM !== name) {
        return item;
      } else {
        obj = { ITEM: item.ITEM, ITEM_INDEX: item.ITEM_INDEX ?? '', USE_YN: 'D' };
        return { ...item, USE_YN: 'D' };
      }
    });
    if (sectionType == '지급') {
      setPayData(list);
    } else {
      setDeducData(list);
    }
    UpdateSections(obj, list);
  };

  // 지급,공제항목 수정
  const UpdateSections = async (
    obj: { ITEM_INDEX: string; ITEM: string; USE_YN: string },
    list: IPayItemInPaystatement[],
  ) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode(
        'post',
        `/auth/${sectionType === '지급' ? 'updatePayItemV2' : 'updateDeducItemV2'}`,
        {
          SELECT_DATE: moment(date).format('YYYYMM'),
          ITEM_INDEX: obj.ITEM_INDEX,
          ITEM: obj.ITEM,
          USE_YN: obj.USE_YN,
        },
      );

      if (data.resultCode === 0) {
        PayUpdateSectionChange(list);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      setLoadingState({ isFetchLoading: false });
      checkErrorLog(err, ` /auth/${sectionType === '지급' ? 'updatePayItemV2' : 'updateDeducItemV2'}`);
    } finally {
    }
  };

  // 항목변경 시 급여 업데이트
  const PayUpdateSectionChange = async (list: IPayItemInPaystatement[]) => {
    try {
      let PAY_ARRAY = [];
      let DEDUC_ARRAY = [];
      if (sectionType === '지급') {
        PAY_ARRAY = list.map((item, index) => {
          return { ITEM: item.ITEM, VALUE: item.VALUE, USE_YN: item.USE_YN };
        });
        DEDUC_ARRAY = deducData.map((item, index) => {
          return {
            ITEM: item.ITEM.replace('_D', ''),
            VALUE: item.VALUE,
            USE_YN: item.USE_YN,
          };
        });
      } else {
        PAY_ARRAY = payData.map((item, index) => {
          return { ITEM: item.ITEM, VALUE: item.VALUE, USE_YN: item.USE_YN };
        });
        DEDUC_ARRAY = list.map((item, index) => {
          return {
            ITEM: item.ITEM.replace('_D', ''),
            VALUE: item.VALUE,
            USE_YN: item.USE_YN,
          };
        });
      }
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updatePaystubMemberV2`, {
        SELECT_DATE: moment(date).format('YYYYMM'),
        DATA: [
          {
            MEMBER_INDEX: selectedEmpInfo?.MEMBER_INDEX,
            SEPARATOR: sectionType === '지급' ? 'Y' : 'N', // 지급수정 'Y', 공제수정 'N'
            PAY_ARRAY: PAY_ARRAY,
            DEDUC_ARRAY: DEDUC_ARRAY,
            PAY_TYPE: payType.value,
          },
        ],
      });

      if (data.resultCode === 0) {
        fetchEmpPay();
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      setLoadingState({ isFetchLoading: false });
      checkErrorLog(err, ' /auth/updatePaystubMemberV2');
    } finally {
    }
  };

  // 월 변경 시 직원목록 패칭 요청
  React.useEffect(() => {
    fetchEmpList();
  }, [date]);

  // 직원목록에서 직원 선택 시 급여정보 패칭 요청
  useEffect(() => {
    if (selectedEmpInfo !== null) {
      fetchEmpPay();
    }
  }, [selectedEmpInfo]);

  useEffect(() => {
    if (sendHistoryData.modalShow === false) {
      setSendHistoryData({
        modalShow: false,
        memberIndex: 0,
        empName: '',
        data: [],
      });
    }
  }, [sendHistoryData.modalShow]);

  return (
    <>
      <WithSideBarAndNavBar>
        <div className="transfer-of-mgt">
          <div className="transfer-of-mgt__head">
            <div>
              <div className="transfer-of-mgt__head__top">
                <div className="transfer-of-mgt__head__nav">
                  급여명세서 <span className="transfer-of-mgt__head__nav-span">{'>'}</span> 전송 및 관리
                </div>
              </div>
              <div className="transfer-of-mgt__head__heading">급여명세서 전송 및 관리</div>

              <div className="transfer-of-mgt__head__description">
                <div className="transfer-of-mgt__head__description__content">
                  - step1. [직원목록]에서 급여명세서를 작성할 직원 이름을 클릭해 주세요.
                </div>
                <div className="transfer-of-mgt__head__description__content">
                  - step2. [급여명세서 작성]에서 해당 직원의 기본급을 입력해 주세요.(공제항목은 자동계산됩니다.)
                </div>
                <div className="transfer-of-mgt__head__description__content">
                  - step3. 작성이 완료되었다면, [직원목록] 좌측 체크박스를 클릭하시고 [카카오톡 발송]을 눌러주세요.
                </div>
              </div>
            </div>
            <div className="transfer-of-mgt__head__buttons">
              <div className="transfer-of-mgt__head__buttons__Area">
                <div className="transfer-of-mgt__head__buttons__Area__stat">
                  <Button label="통계" type="type1" action={fetchStatisticData} />
                </div>
                <div className="transfer-of-mgt__head__buttons__list-of-emp">
                  <Button label="직원 목록" type="type2" action={enterPayEmpListScreen} />
                </div>
              </div>
              <div className="transfer-of-mgt__head__calendar">
                <MonthInput onChange={handleCalendarInput} value={date} />
              </div>
            </div>
          </div>

          <div className="transfer-of-mgt__body">
            <div className="transfer-of-mgt__body__col1">
              <div className="transfer-of-mgt__body__head">
                <div className="transfer-of-mgt__body__head__text">
                  등록된 직원 <span className="transfer-of-mgt__body__head__text-span">{empList.length}</span>
                </div>
                <div className="transfer-of-mgt__body__head__button">
                  <Button label="카카오톡 발송" type="type6" action={clickSendKakao} />
                </div>
              </div>
              <TableWrap>
                <table className="transfer-of-mgt__emp-table">
                  <colgroup>
                    <col width="10%" />
                    <col width="10%" />
                    <col width="25%" />
                    <col width="15%" />
                    <col width="40%" />
                  </colgroup>
                  <thead>
                    <tr className="transfer-of-mgt__emp-table__tr">
                      <th className="transfer-of-mgt__emp-table__td">
                        <CheckBox checked={checkboxEmployeeAllToggle} defaultCheck={allEmployeeSelectCheckbox} />
                      </th>
                      <th className="transfer-of-mgt__emp-table__td">no.</th>
                      <th className="transfer-of-mgt__emp-table__td">이름</th>
                      <th className="transfer-of-mgt__emp-table__td">전송수</th>
                      <th className="transfer-of-mgt__emp-table__td">최근발송</th>
                    </tr>
                  </thead>
                </table>
                <div
                  style={{
                    height: 'calc(80vh - 144px)',
                    borderTop: '1px solid #e9e9e9',
                    overflowX: 'hidden',
                    overflowY: 'scroll',
                  }}>
                  <table className="transfer-of-mgt__emp-table" style={{ width: 'calc(100% + 10px)' }}>
                    <colgroup>
                      <col width="10%" />
                      <col width="50%" />
                      <col width="calc(40% - 17px)" />
                    </colgroup>
                    <tbody style={{ position: 'relative' }}>
                      {empList.length > 0 ? (
                        empList.map((i, index) => (
                          <tr
                            key={`empList_${i.MEMBER_INDEX}_${i.SS_NUMBER}`}
                            className="transfer-of-mgt__emp-table__tr"
                            style={{
                              backgroundColor: i.MEMBER_INDEX === selectedEmpInfo?.MEMBER_INDEX ? '#f0f8f6' : 'white',
                            }}>
                            <td className="transfer-of-mgt__emp-table__td">
                              <CheckBox
                                checked={() => checkboxInviteToggle(i.MEMBER_INDEX)}
                                defaultCheck={i.isChecked ?? false}
                              />
                            </td>
                            <td
                              className={`transfer-of-mgt__emp-table__td emp-table-item ${
                                i.MEMBER_INDEX === selectedEmpInfo?.MEMBER_INDEX ? 'selected' : ''
                              }`}
                              colSpan={3}
                              onClick={() => selectEmp(i)}>
                              <div style={{ display: 'flex', height: 50, alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>{index + 1}</div>
                                {i.RETIRE_DATE ? (
                                  <div style={{ flex: 2.5 }}>
                                    <div>{i.NAME}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#777' }}>{i.RETIRE_DATE}퇴사</div>
                                  </div>
                                ) : (
                                  <div style={{ flex: 2.5 }}>{i.NAME}</div>
                                )}
                                <div style={{ flex: 1.5 }}>{i.MSG_COUNT}</div>
                              </div>
                            </td>
                            <td
                              className="transfer-of-mgt__emp-table__td-content-lite"
                              onClick={() => clickSendHistory(i)}>
                              {moment(i.LAST_PUSH_DATE).isValid()
                                ? moment(i.LAST_PUSH_DATE).format('YYYY.MM.DD HH:mm')
                                : i.LAST_PUSH_DATE}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <EmptyEmpList>
                          <EmptyEmpListText>등록된 직원이 없습니다</EmptyEmpListText>
                          <EmptyEmpListText>화면 우측 상단 직원목록에서 등록해 주세요</EmptyEmpListText>
                        </EmptyEmpList>
                      )}
                    </tbody>
                  </table>
                </div>
              </TableWrap>
            </div>
            <div className="transfer-of-mgt__body__col2" id="SendStatementWrap">
              <div className="transfer-of-mgt__body__head">
                <div className="transfer-of-mgt__body__head__text">
                  급여명세서 작성
                  <span className="transfer-of-mgt__body__head__text-span">
                    [{moment(date).format('YYYY년 MM월')} 급여 {selectedEmpInfo?.NAME ?? ''}]
                  </span>
                </div>
              </div>
              <div className="transfer-of-mgt__payroll-stmnt__description">
                <div className="transfer-of-mgt__head__description__content">
                  - 지급항목을 입력하신 후 입력폼 우측 상단에 "공제계산하기" 버튼을 누르면 공제액이 자동으로 계산됩니다.
                </div>
                <div className="transfer-of-mgt__head__description__content">
                  - 공제항목만 수정하는 경우 저장하기 버튼을 눌러주세요.
                </div>
                <div className="transfer-of-mgt__head__description__content">
                  - 공제계산하기 또는 저장하기를 클릭해야 총공제액,총지급액,실지급액이 합산됩니다.
                </div>
                <div className="transfer-of-mgt__head__description__content">
                  - 국민연금 기준소득월액은 월소득액의 1,000원 미만은 버리고 계산합니다. (국민연금법 시행령 제5조)
                </div>
                <div className="transfer-of-mgt__head__description__content">
                  - 2025년 7월 1일부터 국민연금 하한 기준소득액이 40만원으로 변경되었습니다.
                </div>
                <div className="transfer-of-mgt__head__description__content" style={{ marginLeft: '10px' }}>
                  {`※ 국민연금의 경우 하한 기준소득액보다 낮게 입력할 경우 하한 기준소득액으로 계산됩니다.`}
                </div>
                <div className="transfer-of-mgt__head__description__content">
                  - 2025년 건강보험 하한 기준소득액은 279,266원입니다.
                </div>
                <div className="transfer-of-mgt__head__description__content" style={{ marginLeft: '10px' }}>
                  {`※ 건강보험의 경우 하한 기준소득액보다 낮게 입력할 경우 하한 기준소득액으로 계산됩니다.`}
                </div>
              </div>
              <div className="transfer-of-mgt__payroll-stmnt__buttons">
                <div className="transfer-of-mgt__payroll-stmnt__button">
                  <Button
                    label="지급항목설정"
                    type="type7"
                    icon="CheckListIcon"
                    height={34}
                    action={() => toggleSectionPopup('지급')}
                  />
                </div>
                <div className="transfer-of-mgt__payroll-stmnt__button">
                  <Button
                    label="공제항목설정"
                    type="type7"
                    icon="CheckListIcon"
                    height={34}
                    action={() => toggleSectionPopup('공제')}
                  />
                </div>
                <div className="transfer-of-mgt__payroll-stmnt__button">
                  <Button
                    label="지난달 급여 불러오기"
                    type="type7"
                    icon="cloudIcon"
                    height={34}
                    action={getPrevMonthPayData}
                  />
                </div>
                <div className="transfer-of-mgt__payroll-stmnt__button">
                  <Button
                    label="출퇴근 기반 급여 불러오기"
                    type="type7"
                    icon="downloadIcon"
                    height={34}
                    action={confirmGetRealPayByCommute}
                  />
                </div>
                <div className="transfer-of-mgt__payroll-stmnt__button">
                  <Button label="이번달 급여초기화" type="type7" icon="RefreshIcon" height={34} action={resetPay} />
                </div>
                <div className="transfer-of-mgt__payroll-stmnt__button">
                  <Button
                    label="내역확인"
                    type="type7"
                    icon="RigthCircleIcon"
                    height={34}
                    action={() => enterDetailPayScreen()}
                  />
                </div>
              </div>
              <div className="transfer-of-mgt__payroll-stmnt__buttons">
                <div className="transfer-of-mgt__payroll-stmnt__button-float">
                  <Button
                    label="공제계산하기"
                    type="type2"
                    height={34}
                    action={() => paySaveAndDeducCalculate('지급')}
                  />
                </div>
                <div>
                  <Button label="저장하기" type="type14" height={34} action={() => paySaveAndDeducCalculate('공제')} />
                </div>
              </div>
              <div className="transfer-of-mgt__payroll-stmnt__salary-type">
                <div className="transfer-of-mgt__payroll-stmnt__toggle">
                  <ToggleButton toggled={false} onClick={toggleUseSecondDefaultPay} disabled={false} />
                </div>
                <div>
                  <div className="transfer-of-mgt__payroll-stmnt__toggle__head">기본급 2 사용</div>
                  <div className="transfer-of-mgt__payroll-stmnt__toggle__subhead">
                    (시급이 변경되어 기본급이 2가지인 경우 설정, 기본급2 항목이 추가됩니다.)
                  </div>
                </div>
                <div className="transfer-of-mgt__payroll-stmnt__toggle__select">
                  <Select
                    className="transfer-of-mgt__payroll-stmnt__toggle__dropdown"
                    components={{
                      IndicatorSeparator: () => null,
                      DropdownIndicator: () => (
                        <div style={{ paddingRight: '15px' }}>
                          <ArrowDownIcon />
                        </div>
                      ),
                    }}
                    isSearchable={false}
                    value={payType}
                    styles={colourStyles}
                    options={payTypeOptions}
                    onChange={handlePayType}
                  />
                </div>
              </div>
              <PayTable
                PRO_DATAS={payData}
                PRO_DATAS2={paySecondData}
                DEDUC_DATAS={deducData}
                PAYDATA={payFormData}
                setPAYDATA={setPayFormData}
                PAYDATA2={payFormSecondData}
                setPAYDATA2={setPayFormSecondData}
                empList={empList}
                PAYTYPE={payType.label}
                plusMinusToggle={plusMinusToggle}
                payAutoSum={payAutoSum}
                AddZeroValue={AddZeroValue}
                useSecondDefaultPay={useSecondDefaultPay}
              />
            </div>
          </div>
        </div>
        <CommonModal isVisible={payStatistics.show} onClose={toggleStatisticModal} title={`전월 대비 급여 통계`}>
          <Statistics date={date} data={payStatistics} />
        </CommonModal>
        <CommonModal
          isVisible={sendHistoryData.modalShow}
          onClose={toggleSendHistoryModalVisible}
          title={`${sendHistoryData.empName}직원 급여명세서 전송이력`}>
          <SendHistoryTable data={sendHistoryData.data} enterDetailPayPage={enterDetailPayScreen} />
        </CommonModal>
        <CommonModal isVisible={sectionPopupShow} onClose={toggleSectionPopup} title={`${sectionType}항목 설정`}>
          <SectionPopup
            sectionType={sectionType}
            ToggleSectionPopup={toggleSectionPopup}
            toggleSectionItem={toggleSectionItem}
            data={sectionType === '지급' ? payData : deducData}
            AddSection={addSection}
            DeleteSection={deleteSection}
          />
        </CommonModal>
      </WithSideBarAndNavBar>
    </>
  );
};

export default TransferOfMgt;
